import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import NextPageIcon from '@panel/commons/assets/icons/chevronRightGray.svg';
import LastPageIcon from '@panel/commons/assets/icons/chevronRightGrayDouble.svg';
import { InboxOperations, InboxSelectors } from '@panel/leads/src/components/menuInbox/ducks';
import { FilterSelectors } from '../../../../ducks/filter';
import { PostingsOperations } from '../../../../ducks/posting';

const Pagination = ({ page, setPage, limit, numberOfPostings, isMobile }) => {
  const dispatch = useDispatch();
  const filtersData = useSelector(FilterSelectors.getFiltersState);
  const filtersCriteria = filtersData.searchCriteria;
  const filterSortOnlineFirst = filtersData.filterOnlineFirst;
  const numberOfPages = numberOfPostings > 0 ? Math.ceil(numberOfPostings / limit) : 0;

  const createPagination = (currentPage, totalPages) => {
    let finalArray = [];
    if (currentPage < 4) {
      finalArray = [1];
      // eslint-disable-next-line no-plusplus
      for (let i = 2; i <= 5; i++) {
        if (i <= totalPages) {
          finalArray.push(i);
        }
      }
    } else {
      finalArray = [currentPage - 2, currentPage - 1, currentPage];
      if (currentPage + 2 <= totalPages) {
        finalArray.push(currentPage + 1);
        finalArray.push(currentPage + 2);
      } else if (currentPage + 1 <= totalPages) {
        finalArray.push(currentPage + 1);
        finalArray.unshift(currentPage - 3);
      } else {
        finalArray.unshift(currentPage - 3);
        finalArray.unshift(currentPage - 4);
      }
    }
    return finalArray;
  };

  const createPaginationMobile = (currentPage, totalPages) => {
    let finalArray = [];
    if (currentPage < 3) {
      finalArray = [1];
      // eslint-disable-next-line no-plusplus
      for (let i = 2; i <= 3; i++) {
        if (i <= totalPages) {
          finalArray.push(i);
        }
      }
    } else {
      finalArray = [currentPage - 1, currentPage];
      if (currentPage + 1 <= totalPages) {
        finalArray.push(currentPage + 1);
      } else {
        finalArray.unshift(currentPage - 2);
      }
    }
    return finalArray;
  };

  const pagesArray = isMobile ? createPaginationMobile(page, numberOfPages) : createPagination(page, numberOfPages);

  const handleChange = useCallback(
    selectedPage => {
      const queryParams = {
        page: selectedPage,
        limit
      };
      if (filtersCriteria) {
        queryParams.searchParameters = filtersCriteria;
      }
      if (filterSortOnlineFirst) {
        queryParams.onlineFirst = filterSortOnlineFirst;
      }
      dispatch(PostingsOperations.fetch(queryParams, false, false));
      dispatch(InboxOperations.fetchUnread('nondiscarded', false));

      setPage(selectedPage);
      window.scroll({
        top: 0,
        behavior: 'smooth'
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersCriteria, filterSortOnlineFirst]
  );

  useEffect(() => {
    // reinicializo el valor del currentPage cuando se agrega un filtro/busqueda
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersCriteria, filterSortOnlineFirst]);

  return (
    <PaginationContainer>
      {page > 1 && <SkipAll data-panel='skip-all-left' className='reverse' onClick={() => handleChange(1)} />}
      {page > 1 && <NextPage data-panel='previous-page' className='reverse' onClick={() => handleChange(page - 1)} />}
      {pagesArray.map(pageNumber => (
        <Page
          isMobile={isMobile}
          onClick={() => handleChange(pageNumber)}
          key={`pageNumber-${pageNumber}`}
          selected={pageNumber === page}
          data-panel={`pageNumber-${pageNumber}`}
        >
          {pageNumber}
        </Page>
      ))}
      {page < numberOfPages && (
        <NextPage data-panel='next-page' isMobile={isMobile} onClick={() => handleChange(page + 1)} />
      )}
      {page < numberOfPages && (
        <SkipAll data-panel='skip-all-right' isMobile={isMobile} onClick={() => handleChange(numberOfPages)} />
      )}
    </PaginationContainer>
  );
};

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 40px 0 !important;
`;

const Page = styled.div`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Hind;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0em;
  margin-right: 10px;
  cursor: pointer;
  :hover {
    border: ${({ selected }) => !selected && `1px solid ${select('midLightGray')}`};
  }
  color: ${({ selected }) => !selected && select('darkGray')};
  border: ${({ selected }) => selected && `1px solid ${getColor('primary')}`};
  ${({ isMobile }) =>
    isMobile &&
    css`
      height: 32px;
      width: 32px;
    `}
`;

const NextPage = styled.div`
  background-image: ${`url(${NextPageIcon})`};
  height: 40px;
  width: 40px;
  margin-left: 20px;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  :hover {
    ${({ isMobile }) =>
      !isMobile &&
      css`
        border: 1px solid ${select('midDarkGray')};
      `};
  }
  border-radius: 40px;
  &.reverse {
    transform: scaleX(-1);
    margin: 0 20px 0 0;
  }
`;

const SkipAll = styled.div`
  background-image: ${`url(${LastPageIcon})`};
  height: 40px;
  width: 40px;
  margin-left: 20px;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  :hover {
    ${({ isMobile }) =>
      !isMobile &&
      css`
        border: 1px solid ${select('midDarkGray')};
      `};
  }
  border-radius: 40px;
  &.reverse {
    transform: scaleX(-1);
    margin: 0 20px 0 0;
  }
`;

export default Pagination;
